import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer } from 'react-konva';
import type Konva from 'konva';
import { ChevronDown, Eye, Hand, MousePointer2, RotateCcw, X } from 'lucide-react';
import type { Edge, Node } from '../../../core/models/scene';
import type { DiagramType } from '../../../core/tools/interfaces/ITool';
import type {
  MultiViewEntityDetail,
  MultiViewEntityDetailRow,
  MultiViewEntityRef,
  MultiViewLinkMode,
} from '../../../core/multi-view';
import { cn } from '../../../lib/utils';
import { computeFitToContent } from '../lib/fitToContent';
import { computePanMove, computePanStart, computeWheelZoom } from '../lib/stageInteractions';
import { EdgesRenderer } from '../renderers/EdgesRenderer';
import { NodesRenderer } from '../renderers/NodesRenderer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

export interface ProjectionViewpointOption {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
}

interface ProjectionSceneCanvasProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly controlsSlot?: React.ReactNode;
  readonly viewpoints?: readonly ProjectionViewpointOption[];
  readonly activeViewpointId?: string;
  readonly onViewpointChange?: (viewpointId: string) => void;
  readonly nodes: readonly Node[];
  readonly edges: readonly Edge[];
  readonly diagramType?: DiagramType;
  readonly linkMode?: MultiViewLinkMode;
  readonly selectedEntity?: MultiViewEntityRef | null;
  readonly resolveEntityRef?: (node: Node) => MultiViewEntityRef | null;
  readonly resolveEntityDetail?: (node: Node) => MultiViewEntityDetail | null;
  readonly onSelectEntity?: (entity: MultiViewEntityRef | null, node: Node | null) => void;
  readonly isLoading?: boolean;
  readonly error?: unknown;
  readonly emptyMessage?: string;
  readonly onClose: () => void;
}

const EMPTY_EDGE_VERSIONS = new Map<string, number>();
const noopTwo = () => {};

export const ProjectionSceneCanvas: React.FC<ProjectionSceneCanvasProps> = ({
  title,
  subtitle,
  controlsSlot,
  viewpoints = [],
  activeViewpointId,
  onViewpointChange,
  nodes,
  edges,
  diagramType = 'generic',
  linkMode = 'linked-selection',
  selectedEntity,
  resolveEntityRef,
  resolveEntityDetail,
  onSelectEntity,
  isLoading = false,
  error,
  emptyMessage = 'No projection data to render yet.',
  onClose,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const stageStateRef = useRef({ scale: 1, offsetX: 0, offsetY: 0 });
  const isPanningRef = useRef(false);
  const didPanRef = useRef(false);
  const activePointerIdRef = useRef<number | null>(null);
  const panStartRef = useRef({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [stageState, setStageState] = useState({ scale: 1, offsetX: 0, offsetY: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [isPanMode, setIsPanMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [selectedDetail, setSelectedDetail] = useState<MultiViewEntityDetail | null>(null);
  const activeViewpoint = viewpoints.find((viewpoint) => viewpoint.id === activeViewpointId);

  const sceneKey = useMemo(
    () => `${nodes.map((node) => node.id).join('|')}::${edges.map((edge) => edge.id).join('|')}`,
    [nodes, edges],
  );

  useEffect(() => {
    stageStateRef.current = stageState;
  }, [stageState]);

  useEffect(() => {
    setSelectedIds(new Set());
    setSelectedDetail(null);
  }, [sceneKey]);

  const externallySelectedIds = useMemo(() => {
    if (!selectedEntity || linkMode === 'independent') return [];
    return nodes
      .filter((node) => {
        const ref = resolveEntityRef?.(node) ?? entityRefFromNodeMetadata(node);
        return entityRefsMatch(ref, selectedEntity);
      })
      .map((node) => node.id);
  }, [linkMode, nodes, resolveEntityRef, selectedEntity]);

  const renderedSelectedIds = useMemo(() => {
    if (externallySelectedIds.length === 0) return selectedIds;
    return new Set([...selectedIds, ...externallySelectedIds]);
  }, [externallySelectedIds, selectedIds]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const fitScene = useCallback(() => {
    if (nodes.length === 0 || size.width === 0 || size.height === 0) {
      setStageState({ scale: 1, offsetX: 0, offsetY: 0 });
      return;
    }
    const fit = computeFitToContent([...nodes], size, 80);
    setStageState(fit);
  }, [nodes, size]);

  useEffect(() => {
    fitScene();
  }, [fitScene, sceneKey]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (event: WheelEvent) => {
      event.preventDefault();
      const rect = el.getBoundingClientRect();
      const pointer = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      const current = stageStateRef.current;
      const result = computeWheelZoom(
        current.scale,
        pointer,
        { x: current.offsetX, y: current.offsetY },
        event.deltaY,
      );
      setStageState({
        scale: result.scale,
        offsetX: result.position.x,
        offsetY: result.position.y,
      });
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isPanMode) return;
    if (event.pointerType === 'mouse' && event.button !== 0 && event.button !== 1) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const pointer = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    activePointerIdRef.current = event.pointerId;
    isPanningRef.current = true;
    didPanRef.current = false;
    setIsPanning(true);
    panStartRef.current = computePanStart(
      { x: stageStateRef.current.offsetX, y: stageStateRef.current.offsetY },
      pointer,
    );
  }, [isPanMode]);

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isPanningRef.current) return;
    if (activePointerIdRef.current !== null && event.pointerId !== activePointerIdRef.current) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const pointer = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    const position = computePanMove(panStartRef.current, pointer);
    if (
      Math.abs(position.x - stageStateRef.current.offsetX) > 1 ||
      Math.abs(position.y - stageStateRef.current.offsetY) > 1
    ) {
      didPanRef.current = true;
    }
    setStageState((current) => ({
      ...current,
      offsetX: position.x,
      offsetY: position.y,
    }));
  }, []);

  const stopPanning = useCallback((event?: React.PointerEvent<HTMLDivElement>) => {
    if (
      event &&
      activePointerIdRef.current !== null &&
      event.pointerId !== activePointerIdRef.current
    ) {
      return;
    }
    if (event?.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    isPanningRef.current = false;
    activePointerIdRef.current = null;
    setIsPanning(false);
    if (didPanRef.current) {
      window.setTimeout(() => {
        didPanRef.current = false;
      }, 0);
    }
  }, []);

  const registerNodeRef = useCallback(noopTwo, []);
  const registerEdgeRef = useCallback(noopTwo, []);
  const handleStageMouseDown = useCallback((event: Konva.KonvaEventObject<MouseEvent>) => {
    if (isPanMode) return;
    const stage = event.target.getStage();
    if (!stage || event.target !== stage) return;
    setSelectedIds(new Set());
    setSelectedDetail(null);
    onSelectEntity?.(null, null);
  }, [isPanMode, onSelectEntity]);
  const onNodeClick = useCallback((nodeId: string, event: unknown) => {
    const konvaEvent = event as Konva.KonvaEventObject<MouseEvent> | undefined;
    if (konvaEvent) konvaEvent.cancelBubble = true;
    if (isPanMode || didPanRef.current) return;
    const node = nodes.find((candidate) => candidate.id === nodeId) ?? null;
    setSelectedIds(new Set([nodeId]));
    setSelectedDetail(node && resolveEntityDetail ? resolveEntityDetail(node) : null);
    onSelectEntity?.(
      node ? (resolveEntityRef?.(node) ?? entityRefFromNodeMetadata(node)) : null,
      node,
    );
  }, [isPanMode, nodes, onSelectEntity, resolveEntityDetail, resolveEntityRef]);
  const onEdgeClick = useCallback((edgeId: string, event: unknown) => {
    const konvaEvent = event as Konva.KonvaEventObject<MouseEvent> | undefined;
    if (konvaEvent) konvaEvent.cancelBubble = true;
    if (isPanMode || didPanRef.current) return;
    setSelectedIds(new Set([edgeId]));
  }, [isPanMode]);
  const onDrag = useCallback(noopTwo, []);

  const contentState = (() => {
    if (error) return <CanvasStateMessage tone="error" message="Could not load this projection." />;
    if (isLoading) return <CanvasStateMessage tone="loading" message="Loading projection..." />;
    if (nodes.length === 0) return <CanvasStateMessage tone="empty" message={emptyMessage} />;
    return null;
  })();

  return (
    <div
      className="relative h-full w-full overflow-hidden bg-canvas canvas-grid"
      data-testid="tid.canvas.projection-pane"
    >
      <div className="pointer-events-none absolute left-3 right-3 top-3 z-20 flex items-start">
        <div className="pointer-events-auto flex max-w-full flex-wrap items-center gap-1 rounded-lg border border-border/80 bg-background/95 px-1.5 py-1 shadow-sm backdrop-blur">
          {viewpoints.length > 1 && activeViewpoint && onViewpointChange ? (
            <>
              <ViewpointChooser
                viewpoints={viewpoints}
                activeViewpoint={activeViewpoint}
                onChange={onViewpointChange}
              />
              <span className="mx-0.5 h-5 w-px bg-border" />
            </>
          ) : null}
          <div className="min-w-[140px] max-w-[260px] px-1">
            <div className="truncate text-xs font-semibold text-foreground">{title}</div>
            {subtitle ? (
              <div className="truncate text-[10px] leading-4 text-muted-foreground">{subtitle}</div>
            ) : null}
          </div>
          {controlsSlot ? (
            <>
              <span className="mx-0.5 h-5 w-px bg-border" />
              <div className="min-w-0 max-w-[380px]">{controlsSlot}</div>
            </>
          ) : null}
          <span className="mx-0.5 h-5 w-px bg-border" />
          <IconButton
            label="Select projection items"
            active={!isPanMode}
            onClick={() => setIsPanMode(false)}
          >
            <MousePointer2 className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton
            label="Pan projection"
            active={isPanMode}
            onClick={() => setIsPanMode(true)}
          >
            <Hand className="h-3.5 w-3.5" />
          </IconButton>
          <span className="mx-0.5 h-5 w-px bg-border" />
          <IconButton label="Fit projection" onClick={fitScene}>
            <RotateCcw className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton label="Close projection" onClick={onClose}>
            <X className="h-3.5 w-3.5" />
          </IconButton>
        </div>
      </div>

      <div
        ref={containerRef}
        className="h-full w-full overflow-hidden"
        style={{ cursor: isPanMode ? (isPanning ? 'grabbing' : 'grab') : 'default', touchAction: 'none' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopPanning}
        onPointerCancel={stopPanning}
        onLostPointerCapture={stopPanning}
      >
        {size.width > 0 && size.height > 0 && (
          <Stage
            ref={stageRef}
            width={size.width}
            height={size.height}
            scaleX={stageState.scale}
            scaleY={stageState.scale}
            x={stageState.offsetX}
            y={stageState.offsetY}
            onMouseDown={handleStageMouseDown}
          >
            <Layer>
              <EdgesRenderer
                edges={edges as Edge[]}
                nodes={nodes as Node[]}
                diagramType={diagramType}
                selectedIds={renderedSelectedIds}
                registerEdgeRef={registerEdgeRef}
                versions={EMPTY_EDGE_VERSIONS}
                onEdgeClick={onEdgeClick}
                canWrite={false}
              />
              <NodesRenderer
                nodes={nodes as Node[]}
                edges={edges as Edge[]}
                diagramType={diagramType}
                selectedIds={renderedSelectedIds}
                registerNodeRef={registerNodeRef}
                onNodeClick={onNodeClick}
                onDragStart={onDrag}
                onDragMove={onDrag}
                onDragEnd={onDrag}
                onTransformStart={onDrag}
                onTransformEnd={onDrag}
                canWrite={false}
              />
            </Layer>
          </Stage>
        )}
      </div>
      {contentState}
      {selectedDetail ? (
        <ProjectionEntityInspector
          detail={selectedDetail}
          unresolvedSelection={selectedEntity?.resolved === false}
          onClose={() => {
            setSelectedDetail(null);
            setSelectedIds(new Set());
            onSelectEntity?.(null, null);
          }}
        />
      ) : null}
    </div>
  );
};

const CanvasStateMessage: React.FC<{
  readonly message: string;
  readonly tone: 'empty' | 'error' | 'loading';
}> = ({ message, tone }) => (
  <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
    <div
      className={cn(
        'rounded-lg border bg-background/95 px-4 py-3 text-sm shadow-sm backdrop-blur',
        tone === 'error' ? 'border-destructive/30 text-destructive' : 'border-border text-muted-foreground',
      )}
    >
      {message}
    </div>
  </div>
);

const ViewpointChooser: React.FC<{
  readonly viewpoints: readonly ProjectionViewpointOption[];
  readonly activeViewpoint: ProjectionViewpointOption;
  readonly onChange: (viewpointId: string) => void;
}> = ({ viewpoints, activeViewpoint, onChange }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        className="flex h-7 max-w-[190px] items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        title="Choose projection viewpoint"
      >
        <Eye className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{activeViewpoint.label}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-64">
      {viewpoints.map((viewpoint) => {
        const active = viewpoint.id === activeViewpoint.id;
        return (
          <DropdownMenuItem
            key={viewpoint.id}
            className={cn('items-start gap-2', active && 'bg-accent text-accent-foreground')}
            onSelect={() => onChange(viewpoint.id)}
          >
            <span className={cn(
              'mt-1 h-2 w-2 shrink-0 rounded-full border border-current',
              active ? 'bg-current' : 'bg-transparent',
            )} />
            <span className="min-w-0">
              <span className="block text-sm font-medium">{viewpoint.label}</span>
              {viewpoint.description ? (
                <span className="mt-0.5 block text-xs leading-4 text-muted-foreground">
                  {viewpoint.description}
                </span>
              ) : null}
            </span>
          </DropdownMenuItem>
        );
      })}
    </DropdownMenuContent>
  </DropdownMenu>
);

const ProjectionEntityInspector: React.FC<{
  readonly detail: MultiViewEntityDetail;
  readonly unresolvedSelection?: boolean;
  readonly onClose: () => void;
}> = ({ detail, unresolvedSelection = false, onClose }) => (
  <aside className="pointer-events-auto absolute right-3 top-24 z-30 w-[min(340px,calc(100%-24px))] rounded-lg border border-border bg-background/95 p-3 shadow-lg backdrop-blur">
    <div className="mb-2 flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-foreground">{detail.title}</div>
        {detail.subtitle ? (
          <div className="mt-0.5 truncate text-xs text-muted-foreground">{detail.subtitle}</div>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Close details"
        onClick={onClose}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
    {detail.badges && detail.badges.length > 0 ? (
      <div className="mb-2 flex flex-wrap gap-1">
        {detail.badges.map((badge) => (
          <span
            key={badge}
            className={cn(
              'rounded border px-1.5 py-0.5 text-[10px] font-medium',
              detail.tone === 'danger' && 'border-red-200 bg-red-50 text-red-700',
              detail.tone === 'warning' && 'border-amber-200 bg-amber-50 text-amber-700',
              detail.tone === 'success' && 'border-emerald-200 bg-emerald-50 text-emerald-700',
              (!detail.tone || detail.tone === 'default') && 'border-border bg-muted/50 text-muted-foreground',
            )}
          >
            {badge}
          </span>
        ))}
      </div>
    ) : null}
    {detail.emptyMessage ? (
      <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        {detail.emptyMessage}
      </div>
    ) : null}
    {unresolvedSelection ? (
      <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        No matching primary org-tree node was found for this projection item.
      </div>
    ) : null}
    {detail.rows && detail.rows.length > 0 ? (
      <ProjectionDetailRows rows={detail.rows} />
    ) : null}
    {detail.sections?.map((section) => (
      <div key={section.id} className="mt-3 border-t border-border pt-2">
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {section.title}
        </div>
        <ProjectionDetailRows rows={section.rows} />
      </div>
    ))}
  </aside>
);

const ProjectionDetailRows: React.FC<{
  readonly rows: readonly MultiViewEntityDetailRow[];
}> = ({ rows }) => (
  <div className="space-y-1">
    {rows.map((row) => (
      <div key={row.id} className="flex items-start justify-between gap-3 text-xs">
        <span className="shrink-0 text-muted-foreground">{row.label}</span>
        <span className={cn(
          'min-w-0 flex-1 truncate text-right font-medium text-foreground',
          row.tone === 'muted' && 'text-muted-foreground',
          row.tone === 'success' && 'text-emerald-700',
          row.tone === 'warning' && 'text-amber-700',
          row.tone === 'danger' && 'text-red-700',
        )}>
          {row.value}
        </span>
      </div>
    ))}
  </div>
);

const IconButton: React.FC<{
  readonly label: string;
  readonly active?: boolean;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}> = ({ label, active = false, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    className={cn(
      'flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
      active && 'bg-muted text-foreground',
    )}
  >
    {children}
  </button>
);

function entityRefFromNodeMetadata(node: Node): MultiViewEntityRef | null {
  const attrs = node.attrs ?? {};
  const metadata = firstRecord(attrs.atlasGantt, attrs.atlasPlanning, attrs.atlasDiff, attrs.blueprintReview);
  if (!metadata) return null;

  const aggregateField = stringValue(metadata.aggregateField);
  const aggregateValue = stringValue(metadata.aggregateValue);
  if (aggregateField && aggregateValue) {
    return {
      id: `aggregate:${aggregateField}:${aggregateValue}`,
      kind: 'aggregate',
      domain: 'atlas',
      label: `${titleCase(aggregateField)}: ${aggregateValue}`,
    };
  }

  const personId = stringValue(metadata.personExternalId) ?? stringValue(metadata.personId);
  if (personId) {
    return {
      id: personId,
      kind: 'person',
      domain: 'atlas',
      label: stringValue(metadata.personLabel) ?? stringValue(node.name) ?? personId,
    };
  }

  const targetId = stringValue(metadata.targetExternalId);
  if (targetId) {
    return {
      id: targetId,
      kind: stringValue(metadata.targetKind) ?? 'work_target',
      domain: 'atlas',
      label: stringValue(metadata.targetLabel) ?? stringValue(node.name) ?? targetId,
    };
  }

  const resourceId = stringValue(metadata.resourceId);
  if (resourceId) {
    return {
      id: resourceId,
      kind: 'resource',
      domain: 'blueprint',
      label: stringValue(metadata.resourceLabel) ?? stringValue(node.name) ?? resourceId,
    };
  }

  return null;
}

function entityRefsMatch(
  left: MultiViewEntityRef | null | undefined,
  right: MultiViewEntityRef | null | undefined,
): boolean {
  if (!left || !right) return false;
  if (left.domain && right.domain && left.domain !== right.domain) return false;
  if (left.kind && right.kind && left.kind !== right.kind) return false;
  return left.id === right.id;
}

function firstRecord(...values: readonly unknown[]): Record<string, unknown> | null {
  return values.find((value): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value),
  ) ?? null;
}

function stringValue(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const text = String(value).trim();
  return text ? text : null;
}

function titleCase(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
